import { Trans, useLingui } from "@lingui/react/macro";
import { useQuery } from "@tanstack/react-query";
import { ChevronDown, Plus } from "lucide-react";
import { observer } from "mobx-react-lite";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { apiGetHfExamsByPatientIdOptions } from "../../../api";
import { AppModel } from "../../../app/app-model";
import { useSupportedMedicalData } from "../../../catalogs/supported-medical-data";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../../components/ui-kit/base/dropdown-menu";
import { Button } from "../../../components/ui-kit/button";
import { CellLastEdit } from "../../../components/ui-kit/tables/cells/cell-last-edit";
import { DataTable } from "../../../components/ui-kit/tables/data-table";
import { getVariableByName } from "../../../utils/data-utils";
import { requireNonNull } from "../../../utils/utils";
import { CellGenericValue } from "../common/cell-generic-value";
import { CellGenericValueField } from "../common/cell-generic-value-field";
import { MedData, MedDataItem } from "../common/med-data";
import { PatientSubpage } from "../common/patient-subpage";

export const ExamsTable = observer(function ExamsTable(props: {
  app: AppModel;
}) {
  const { t } = useLingui();
  const navigate = useNavigate();
  const patient = props.app.requirePatient();
  const api = props.app.seerlinqApi;
  const supportedMedData = useSupportedMedicalData();
  const [model] = useState(
    () => new MedData<MedDataItem>([], api.permissions, api.requireUserUUID()),
  );
  const { data, refetch } = useQuery(
    apiGetHfExamsByPatientIdOptions({
      path: { patient_id: patient.patientId },
    }),
  );

  useEffect(() => {
    if (data) model.updateData(data.medical_data);
  }, [data, model]);

  return (
    <PatientSubpage title={t`Exams`} loading={!model.initialized}>
      <DataTable
        filterActions={
          // Several kinds of record behind one Add: the button opens a menu.
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="primary" icon={Plus}>
                <Trans>Add exams data</Trans>
                <ChevronDown aria-hidden />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              {[
                { label: t`Other (ECG / final outcome)`, href: "add-other" },
                { label: t`ECHO`, href: "add-echo" },
                { label: t`RHC`, href: "add-rhc" },
                { label: t`LHC`, href: "add-lhc" },
              ].map((item) => (
                <DropdownMenuItem
                  key={item.href}
                  onSelect={() => void navigate(item.href)}
                >
                  {item.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        }
        persistState
        editMode="dialog"
        model={model}
        itemKeyField="uuid"
        columns={[
          {
            header: <Trans>Measured</Trans>,
            attr: "measurement_datetime",
            type: "datetime",
            editable: true,
            sort: true,
            initSort: "desc",
          },
          {
            header: <Trans>Type</Trans>,
            attr: "measurement_type",
            type: "string",
            sort: true,
          },
          {
            header: <Trans>Value</Trans>,
            attr: "measurement_value",
            editable: true,
            editInDialog: true,
            cell: (p) => (
              <>
                {!p.patch && (
                  <CellGenericValue
                    {...p}
                    attr={requireNonNull(p.attr)}
                    variableDef={getVariableByName(
                      supportedMedData,
                      p.item.measurement_type,
                    )}
                  />
                )}
                {p.patch && (
                  <CellGenericValueField
                    model={p.patch}
                    attr={requireNonNull(p.attr)}
                    variableDef={getVariableByName(
                      supportedMedData,
                      p.item.measurement_type,
                    )}
                  />
                )}
              </>
            ),
          },
          {
            header: <Trans>Unit</Trans>,
            attr: "measurement_unit",
            type: "string",
          },
          {
            header: <Trans>Comment</Trans>,
            attr: "comment",
            type: "string",
            editable: true,
            multiline: true,
          },
          model.showingHistory && {
            header: <Trans>Added</Trans>,
            attr: "created_at",
            type: "datetime",
          },
          model.showingHistory && {
            header: <Trans>Last edit</Trans>,
            cell: ({ item }) => <CellLastEdit item={item} />,
          },
        ]}
        onEdit={async (editedField, item) => {
          await patient.updateItem("medicaldata", item.uuid, editedField);
          await refetch();
        }}
        onDelete={async (item) => {
          await patient.deleteItem("medicaldata", item.uuid);
          await refetch();
        }}
      />
    </PatientSubpage>
  );
});
